import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Prescription } from '../../types';
import { Pill, CheckCircle, XCircle, Printer, Check, X, FileText } from 'lucide-react';
export const PharmacyDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  // Use ID pattern for live updates
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Local state to track dispensing before saving
  const [dispenseStatus, setDispenseStatus] = useState<Record<string, 'Dispensed' | 'Not Dispensed'>>({});
  // State for showing the prescription preview modal
  const [showPrescriptionPreview, setShowPrescriptionPreview] = useState(false);
  const pendingEncounters = encounters.filter(e => e.stage === 'PHARMACY');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    const initialStatus: Record<string, 'Dispensed' | 'Not Dispensed'> = {};
    encounter.prescriptions.forEach(rx => {
      if (rx.status !== 'Pending') {
        initialStatus[rx.id] = rx.status;
      }
    });
    setDispenseStatus(initialStatus);
    setShowPrescriptionPreview(false);
  };
  const handleToggleStatus = (rxId: string, status: 'Dispensed' | 'Not Dispensed') => {
    setDispenseStatus(prev => ({
      ...prev,
      [rxId]: status
    }));
  };
  const handleCompleteDispensing = () => {
    if (!selectedEncounter || !currentUser) return;
    // Check if all drugs have a status
    const allProcessed = selectedEncounter.prescriptions.every(rx => dispenseStatus[rx.id]);
    if (!allProcessed) {
      alert('Please mark all drugs as Dispensed or Not Available.');
      return;
    }
    const updatedPrescriptions = selectedEncounter.prescriptions.map(rx => ({
      ...rx,
      status: dispenseStatus[rx.id],
      dispensedAt: new Date().toISOString(),
      dispensedBy: currentUser.id,
      pharmacistName: currentUser.fullName
    }));
    const hasUndispensed = updatedPrescriptions.some(rx => rx.status === 'Not Dispensed');
    updateEncounter(selectedEncounter.id, {
      prescriptions: updatedPrescriptions,
      stage: 'CLINICIAN'
    });
    alert(hasUndispensed ? 'Dispensing completed. Report sent back to Clinician for review (some drugs were not available).' : 'Dispensing completed. Report sent back to Clinician.');
    setSelectedEncounterId(null);
    setDispenseStatus({});
    setShowPrescriptionPreview(false);
  };
  const handlePrint = () => {
    window.print();
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-1395">Patient not found</div>;
    const notDispensedDrugs = selectedEncounter.prescriptions.filter(rx => dispenseStatus[rx.id] === 'Not Dispensed');
    const allProcessed = selectedEncounter.prescriptions.every(rx => dispenseStatus[rx.id]);
    // If showing the prescription preview
    if (showPrescriptionPreview) {
      return <div className="space-y-6 pb-20" data-id="element-1396">
          <div className="flex justify-between items-center print:hidden sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-1397">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1398">
              Prescription Preview
            </h1>
            <div className="flex space-x-2" data-id="element-1399">
              <Button variant="outline" onClick={() => setShowPrescriptionPreview(false)} data-id="element-1400">
                Back to Dispensing
              </Button>
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700" data-id="element-1401">
                <Printer className="w-4 h-4 mr-2" data-id="element-1402" /> Print Prescription
              </Button>
            </div>
          </div>

          {/* A4 Prescription Form Container */}
          <div className="flex justify-center print:block print:m-0" data-id="element-1403">
            <div className="bg-white shadow-lg print:shadow-none w-full max-w-[210mm] min-h-[297mm] p-10 print:p-0 print:w-full print:max-w-none border border-gray-200 print:border-none" data-id="element-1404">
              {/* Form Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-6" data-id="element-1405">
                <div className="flex items-center" data-id="element-1406">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-24 w-24 object-contain mr-4" data-id="element-1407" />
                  <div data-id="element-1408">
                    <h1 className="text-3xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-1409">
                      BMED CARE
                    </h1>
                    <p className="text-sm italic text-gray-600" data-id="element-1410">
                      Maternity Care Made Better
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-1411">
                  <p className="font-bold text-xl uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-1412">
                    PRESCRIPTION FORM
                  </p>
                  <p className="text-sm" data-id="element-1413">
                    <span className="font-semibold" data-id="element-1414">Date:</span>{' '}
                    {new Date().toLocaleDateString()}
                  </p>
                  <p className="text-sm" data-id="element-1415">
                    <span className="font-semibold" data-id="element-1416">File No:</span>{' '}
                    {patient.bmcFileNumber}
                  </p>
                </div>
              </div>

              {/* Patient Details */}
              <div className="border-2 border-black p-4 mb-8 text-sm space-y-3" data-id="element-1417">
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1418">
                  <div className="col-span-8" data-id="element-1419">
                    <span className="font-semibold" data-id="element-1420">Patient Name:</span>{' '}
                    {patient.firstName} {patient.lastName}
                  </div>
                  <div className="col-span-2" data-id="element-1421">
                    <span className="font-semibold" data-id="element-1422">Age:</span> {patient.age}
                  </div>
                  <div className="col-span-2" data-id="element-1423">
                    <span className="font-semibold" data-id="element-1424">Sex:</span> {patient.sex}
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1425">
                  <div className="col-span-12" data-id="element-1426">
                    <span className="font-semibold" data-id="element-1427">Allergies:</span>{' '}
                    <span className="text-red-600 font-bold" data-id="element-1428">
                      {patient.allergies}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2" data-id="element-1429">
                  <div className="col-span-12" data-id="element-1430">
                    <span className="font-semibold" data-id="element-1431">
                      Prescribing Clinician:
                    </span>{' '}
                    {selectedEncounter.lastAttendingClinician}
                  </div>
                </div>
              </div>

              {/* Drugs Table */}
              <div className="mb-12" data-id="element-1432">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-1433">
                  Medication to be Sourced Externally
                </h3>
                <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-1434">
                  <thead data-id="element-1435">
                    <tr className="bg-gray-100" data-id="element-1436">
                      <th className="border border-black p-2 w-12" data-id="element-1437">No.</th>
                      <th className="border border-black p-2 text-left" data-id="element-1438">
                        Drug Name
                      </th>
                      <th className="border border-black p-2" data-id="element-1439">Dose</th>
                      <th className="border border-black p-2" data-id="element-1440">Route</th>
                      <th className="border border-black p-2" data-id="element-1441">Frequency</th>
                      <th className="border border-black p-2" data-id="element-1442">Days</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1443">
                    {notDispensedDrugs.map((rx, i) => <tr key={rx.id} data-id="element-1444">
                        <td className="border border-black p-3 font-medium" data-id="element-1445">
                          {i + 1}
                        </td>
                        <td className="border border-black p-3 text-left font-bold" data-id="element-1446">
                          {rx.drugName}
                        </td>
                        <td className="border border-black p-3" data-id="element-1447">{rx.dose}</td>
                        <td className="border border-black p-3" data-id="element-1448">{rx.route}</td>
                        <td className="border border-black p-3" data-id="element-1449">
                          {rx.frequency}
                        </td>
                        <td className="border border-black p-3" data-id="element-1450">{rx.days}</td>
                      </tr>)}
                    {/* Empty rows for padding if few drugs */}
                    {Array.from({
                    length: Math.max(0, 5 - notDispensedDrugs.length)
                  }).map((_, i) => <tr key={`empty-${i}`} data-id="element-1451">
                        <td className="border border-black p-4" data-id="element-1452"></td>
                        <td className="border border-black p-4" data-id="element-1453"></td>
                        <td className="border border-black p-4" data-id="element-1454"></td>
                        <td className="border border-black p-4" data-id="element-1455"></td>
                        <td className="border border-black p-4" data-id="element-1456"></td>
                        <td className="border border-black p-4" data-id="element-1457"></td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              {/* Footer Signatures */}
              <div className="mt-auto pt-10" data-id="element-1458">
                <div className="flex justify-between items-end" data-id="element-1459">
                  <div className="w-64" data-id="element-1460">
                    <p className="text-sm font-semibold mb-8" data-id="element-1461">
                      Pharmacist Signature:
                    </p>
                    <div className="border-b border-black" data-id="element-1462"></div>
                    <p className="text-sm mt-2 font-medium" data-id="element-1463">
                      {currentUser?.fullName}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1464">
                      BMED Pharmacy Dept
                    </p>
                  </div>
                  <div className="w-64" data-id="element-1465">
                    <p className="text-sm font-semibold mb-8" data-id="element-1466">
                      Official Stamp:
                    </p>
                    <div className="border border-black h-24 w-24 rounded-full mx-auto opacity-20" data-id="element-1467"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>;
    }
    // Standard Dispensing View
    return <div className="space-y-6 pb-20 print:hidden" data-id="element-1468">
        <div className="flex justify-between items-center sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-1469">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1470">
            Dispense Medication
          </h1>
          <div className="flex space-x-2" data-id="element-1471">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-1472">
              Back to Queue
            </Button>
            {notDispensedDrugs.length > 0 && <Button variant="outline" onClick={() => setShowPrescriptionPreview(true)} className="border-blue-600 text-blue-600 hover:bg-blue-50" data-id="element-1473">
                <FileText className="w-4 h-4 mr-2" data-id="element-1474" /> Generate Prescription
              </Button>}
            <Button onClick={handleCompleteDispensing} disabled={!allProcessed} className={`${allProcessed ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-400 cursor-not-allowed'} text-white`} data-id="element-1475">
              <Check className="w-5 h-5 mr-2" data-id="element-1476" /> Complete Dispensing
            </Button>
          </div>
        </div>

        {/* Medical Form Style Container */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-1477">
          {/* Patient Header */}
          <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-1478">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1479">
              <div className="col-span-8" data-id="element-1480">
                <span className="font-semibold" data-id="element-1481">Patient Name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-2" data-id="element-1482">
                <span className="font-semibold" data-id="element-1483">Age:</span> {patient.age}
              </div>
              <div className="col-span-2" data-id="element-1484">
                <span className="font-semibold" data-id="element-1485">Sex:</span> {patient.sex}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1486">
              <div className="col-span-6" data-id="element-1487">
                <span className="font-semibold" data-id="element-1488">File No:</span>{' '}
                {patient.bmcFileNumber}
              </div>
              <div className="col-span-6" data-id="element-1489">
                <span className="font-semibold" data-id="element-1490">Prescribing Clinician:</span>{' '}
                {selectedEncounter.lastAttendingClinician}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-1491">
              <div className="col-span-12" data-id="element-1492">
                <span className="font-semibold" data-id="element-1493">Allergies:</span>{' '}
                <span className="text-red-600 font-bold" data-id="element-1494">
                  {patient.allergies}
                </span>
              </div>
            </div>
          </div>

          {/* DRUG TREATMENT / PRESCRIPTION */}
          <div className="border-2 border-black" data-id="element-1495">
            <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-1496">
              Drug Treatment / Prescription
            </h3>
            <table className="w-full border-collapse text-sm text-center" data-id="element-1497">
              <thead data-id="element-1498">
                <tr className="bg-gray-100" data-id="element-1499">
                  <th className="border border-black p-2 w-10" data-id="element-1500">No.</th>
                  <th className="border border-black p-2 text-left" data-id="element-1501">
                    Drug Name
                  </th>
                  <th className="border border-black p-2" data-id="element-1502">Dose</th>
                  <th className="border border-black p-2" data-id="element-1503">Route</th>
                  <th className="border border-black p-2" data-id="element-1504">Frequency</th>
                  <th className="border border-black p-2" data-id="element-1505">Days</th>
                  <th className="border border-black p-2 w-64" data-id="element-1506">
                    Dispense Status
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1507">
                {selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={dispenseStatus[rx.id] === 'Not Dispensed' ? 'bg-red-50' : dispenseStatus[rx.id] === 'Dispensed' ? 'bg-green-50' : ''} data-id="element-1508">
                    <td className="border border-black p-2 font-medium" data-id="element-1509">
                      {i + 1}
                    </td>
                    <td className="border border-black p-2 text-left font-bold" data-id="element-1510">
                      {rx.drugName}
                    </td>
                    <td className="border border-black p-2" data-id="element-1511">{rx.dose}</td>
                    <td className="border border-black p-2" data-id="element-1512">{rx.route}</td>
                    <td className="border border-black p-2" data-id="element-1513">{rx.frequency}</td>
                    <td className="border border-black p-2" data-id="element-1514">{rx.days}</td>
                    <td className="border border-black p-2" data-id="element-1515">
                      <div className="flex space-x-1 justify-center" data-id="element-1516">
                        <button className={`px-3 py-1.5 rounded flex items-center text-xs font-bold transition-colors ${dispenseStatus[rx.id] === 'Dispensed' ? 'bg-green-600 text-white shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`} onClick={() => handleToggleStatus(rx.id, 'Dispensed')} data-id="element-1517">
                          <CheckCircle className="w-3 h-3 mr-1" data-id="element-1518" /> Dispensed
                        </button>
                        <button className={`px-3 py-1.5 rounded flex items-center text-xs font-bold transition-colors ${dispenseStatus[rx.id] === 'Not Dispensed' ? 'bg-red-600 text-white shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`} onClick={() => handleToggleStatus(rx.id, 'Not Dispensed')} data-id="element-1519">
                          <XCircle className="w-3 h-3 mr-1" data-id="element-1520" /> Not Avail
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* NAME OF MEDICAL PRACTITIONER */}
          <div className="border-2 border-black p-4 mt-6" data-id="element-1521">
            <p className="font-bold text-sm" data-id="element-1522">
              Dispensing Pharmacist:{' '}
              <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-1523">
                {currentUser?.fullName || ''}
              </span>
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6 print:hidden" data-id="element-1524">
      <div className="flex justify-between items-center" data-id="element-1525">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1526">Pharmacy Dashboard</h1>
        <Badge variant="success" className="text-sm px-3 py-1 bg-teal-100 text-teal-800" data-id="element-1527">
          {pendingEncounters.length} Prescriptions Waiting
        </Badge>
      </div>

      <Card noPadding data-id="element-1528">
        <ul className="divide-y divide-gray-200" data-id="element-1529">
          {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1530">
                  <div className="flex items-center" data-id="element-1531">
                    <div className="bg-teal-100 p-2 rounded-full mr-4" data-id="element-1532">
                      <Pill className="h-6 w-6 text-teal-600" data-id="element-1533" />
                    </div>
                    <div data-id="element-1534">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1535">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1536">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1537">
                        {encounter.prescriptions.length} items prescribed by{' '}
                        {encounter.lastAttendingClinician}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectEncounter(encounter)} className="bg-teal-600 hover:bg-teal-700 text-white" data-id="element-1538">
                    View Prescription
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1539">
              <Pill className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1540" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1541">
                No pending prescriptions
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};